import { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  RefreshCw, Activity, AlertCircle, CheckCircle2, AlertTriangle, Database,
  Wallet, Banknote, ArrowUpDown, Layers, Scale, ShieldAlert, HelpCircle,
  Download, Building2,
} from "lucide-react";
import api, { formatCurrency } from "@/lib/api";
import { toast } from "sonner";

const STATUS_BADGE = {
  ok: { classes: "border-emerald-500/40 text-emerald-400 bg-emerald-500/10", icon: CheckCircle2, label: "OK" },
  warning: { classes: "border-yellow-500/40 text-yellow-400 bg-yellow-500/10", icon: AlertTriangle, label: "Atenção" },
  critical: { classes: "border-red-500/40 text-red-400 bg-red-500/10", icon: AlertCircle, label: "Crítico" },
  unknown: { classes: "border-white/15 text-muted-foreground bg-white/5", icon: Activity, label: "Indisponível" },
};

const HEALTH_GRADE_COLOR = {
  A: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
  B: "text-cyan-400 border-cyan-500/40 bg-cyan-500/10",
  C: "text-yellow-400 border-yellow-500/40 bg-yellow-500/10",
  D: "text-red-400 border-red-500/40 bg-red-500/10",
};

const EVENT_STATUS_BADGE = {
  matched: { classes: "border-emerald-500/40 text-emerald-400 bg-emerald-500/10", label: "Conciliado" },
  pending: { classes: "border-yellow-500/40 text-yellow-400 bg-yellow-500/10", label: "Pendente" },
  failed: { classes: "border-red-500/40 text-red-400 bg-red-500/10", label: "Falhou" },
  attention: { classes: "border-orange-500/40 text-orange-400 bg-orange-500/10", label: "Atenção" },
  audit_only: { classes: "border-slate-500/40 text-slate-400 bg-slate-500/10", label: "Informativo" },
  skipped: { classes: "border-white/15 text-muted-foreground bg-white/5", label: "Ignorado" },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_BADGE[status] || STATUS_BADGE.unknown;
  const Icon = cfg.icon;
  return (
    <Badge variant="outline" className={`gap-1 ${cfg.classes}`} data-testid={`recon-status-${status}`}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </Badge>
  );
};

const KIND_LABELS = {
  "internal.closing.cashin": "PIX Recebido",
  "starkbank.deposit.created": "PIX Recebido",
  "starkbank.transfer.success": "Transferência Enviada",
  "starkbank.transfer.failed": "Transferência Falhou",
  "starkbank.transfer.reversed": "Estorno de Transferência",
  "internal.withdrawal.requested": "Resgate Solicitado",
  "internal.withdrawal.refunded": "Resgate Estornado",
  "internal.admin.withdrawal": "Saque Margem Kredz",
  "internal.admin.margin_collected": "Margem Coletada",
  "internal.fee.cashin": "Taxa Cashin",
  "internal.fee.cashout": "Taxa Cashout",
  "internal.transfer.canceled": "Transferência Cancelada",
  "starkbank.partial_refund": "Devolução PIX Parcial",
  "starkbank.fee_charged": "Tarifa StarkBank",
  "starkbank.embedded_fee_charged": "Tarifa StarkBank (embutida)",
  "starkbank.unclassified_movement": "Mov. Não Classificado",
  "starkbank.transaction.chargeback": "Chargeback",
  "starkbank.transaction.return": "Return StarkBank",
  "manual.business_decision": "Ajuste Manual",
  "manual.reconcile": "Reconciliação Manual",
};

const formatTime = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit", month: "2-digit", year: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
};

const HealthScoreCard = ({ score }) => {
  if (!score) return null;
  const gradeColor = HEALTH_GRADE_COLOR[score.grade] || HEALTH_GRADE_COLOR.D;
  return (
    <Card className="bg-gradient-to-br from-card to-black/30 border-white/10" data-testid="health-score-card">
      <CardContent className="p-6">
        <div className="flex items-center gap-6">
          <div className={`flex items-center justify-center w-24 h-24 rounded-2xl border-2 ${gradeColor}`}>
            <div className="text-center">
              <p className="text-3xl font-bold leading-none">{score.score}</p>
              <p className="text-xs uppercase tracking-wider mt-0.5">Score</p>
            </div>
          </div>
          <div className="flex-1">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Saúde do Ledger</p>
            <p className="text-2xl font-bold mt-1" data-testid="health-score-grade">Grade {score.grade}</p>
            <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
              Agregação ponderada: projeção (40%), entries órfãs (30%), drift de saldo (30%).
              Métrica indicativa para inspeção rápida.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const DriftCard = ({ title, count, status, description, icon: Icon, dataTestId }) => (
  <Card className="bg-card border-white/10 card-interactive" data-testid={dataTestId}>
    <CardContent className="p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center">
            <Icon className="w-4 h-4 text-blue-400" />
          </div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</p>
        </div>
        <StatusBadge status={status} />
      </div>
      <p className="text-3xl font-bold tracking-tight" data-testid={`${dataTestId}-count`}>
        {count == null ? "—" : count}
      </p>
      <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">{description}</p>
    </CardContent>
  </Card>
);

const BalanceRow = ({ label, value, valueClass = "", testId }) => (
  <div className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className={`text-sm font-mono ${valueClass}`} data-testid={testId}>{value}</span>
  </div>
);

/* ── Operations Advisory Banner ─────────────────────────────────────────── */

const OperationsAdvisoryBanner = () => {
  const [advisory, setAdvisory] = useState(null);

  useEffect(() => {
    api.get("/ledger/admin/operations-advisory")
      .then(r => setAdvisory(r.data))
      .catch(() => {});
  }, []);

  if (!advisory || advisory.value !== "blocked") return null;

  return (
    <Card className="border-red-500/40 bg-red-500/8" data-testid="operations-advisory-banner">
      <CardContent className="p-4 flex items-start gap-3">
        <ShieldAlert className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-red-400">Operações pausadas por reconciliação pendente</p>
          <p className="text-xs text-muted-foreground mt-1">
            Saques e pagamentos estão bloqueados até que a divergência de saldo seja resolvida.
            {advisory.reason && <span className="block mt-0.5 font-mono text-[10px]">{advisory.reason}</span>}
            {advisory.updated_at && <span className="block mt-0.5">Desde: {formatTime(advisory.updated_at)}</span>}
          </p>
          <p className="text-[10px] text-muted-foreground/60 mt-2 italic">
            Para liberar: POST /api/ledger/admin/operations-advisory/clear (4-eyes, master admin)
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

/* ── Unclassified Movements Tab ─────────────────────────────────────────── */

const UnclassifiedMovements = () => {
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/ledger/admin/unclassified-movements", { params: { page, page_size: 25 } });
      setItems(r.data.items || []);
      setPagination(r.data.pagination);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao carregar movimentos");
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card className="border-white/10" data-testid="unclassified-movements-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <HelpCircle className="w-4 h-4 text-orange-400" /> Movimentos não classificados
            {pagination && <span className="text-xs font-normal text-muted-foreground">({pagination.total} total)</span>}
          </CardTitle>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} className="border-white/10 gap-1.5"
                  data-testid="unclassified-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Atualizar
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8" data-testid="unclassified-empty">
            Nenhum movimento não classificado pendente.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="unclassified-table">
              <thead>
                <tr className="border-b border-white/10 text-left text-xs text-muted-foreground">
                  <th className="py-2 pr-3 font-medium">Quando</th>
                  <th className="py-2 pr-3 font-medium">Transaction ID</th>
                  <th className="py-2 pr-3 font-medium">Source</th>
                  <th className="py-2 pr-3 font-medium">Valor</th>
                  <th className="py-2 pr-3 font-medium">Descrição</th>
                  <th className="py-2 pr-3 font-medium">Projetado</th>
                </tr>
              </thead>
              <tbody>
                {items.map((m) => (
                  <tr key={m.id} className="border-b border-white/5 hover:bg-white/5 transition-colors"
                      data-testid={`unclassified-row-${m.id}`}>
                    <td className="py-2.5 pr-3 text-xs font-mono text-muted-foreground">{formatTime(m.created_at)}</td>
                    <td className="py-2.5 pr-3 text-xs font-mono truncate max-w-[120px]" title={m.transaction_id}>{m.transaction_id || "—"}</td>
                    <td className="py-2.5 pr-3 text-xs font-mono truncate max-w-[160px]" title={m.source}>{m.source || "—"}</td>
                    <td className="py-2.5 pr-3 text-xs font-mono">
                      {m.amount_cents != null ? formatCurrency(m.amount_cents / 100) : "—"}
                    </td>
                    <td className="py-2.5 pr-3 text-xs truncate max-w-[180px]" title={m.description}>{m.description || "—"}</td>
                    <td className="py-2.5 pr-3">
                      {m.processed_at ? (
                        <Badge variant="outline" className="border-emerald-500/40 text-emerald-400 bg-emerald-500/10">Sim</Badge>
                      ) : (
                        <Badge variant="outline" className="border-yellow-500/40 text-yellow-400 bg-yellow-500/10">Não</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {pagination && pagination.total > pagination.page_size && (
          <div className="flex items-center justify-end gap-2 mt-4">
            <Button variant="outline" size="sm" disabled={page === 1 || loading} onClick={() => setPage(page - 1)}
                    className="border-white/10" data-testid="unclassified-prev-btn">Anterior</Button>
            <Button variant="outline" size="sm" disabled={page * pagination.page_size >= pagination.total || loading}
                    onClick={() => setPage(page + 1)} className="border-white/10" data-testid="unclassified-next-btn">Próximo</Button>
          </div>
        )}
        {items.length > 0 && (
          <p className="text-[10px] text-muted-foreground/60 mt-3 italic">
            Para reconciliar: use POST /api/ledger/admin/events com action=credit_company ou debit_company (4-eyes obrigatório).
          </p>
        )}
      </CardContent>
    </Card>
  );
};

/* ── Main Page ──────────────────────────────────────────────────────────── */

export default function AdminReconciliation() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [eventStatus, setEventStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [activeTab, setActiveTab] = useState("overview");
  const [companyId, setCompanyId] = useState("");
  const [companies, setCompanies] = useState([]);

  // Load company list once for the filter dropdown
  useEffect(() => {
    api.get("/admin/companies-list").then(r => {
      setCompanies((r.data || []).sort((a, b) => (a.name || "").localeCompare(b.name || "")));
    }).catch(() => {});
  }, []);

  const load = useCallback(async ({ refresh = false } = {}) => {
    setLoading(true);
    try {
      const r = await api.get("/ledger/admin/reconciliation", {
        params: {
          event_status: eventStatus,
          page,
          page_size: 25,
          ...(refresh ? { refresh: true } : {}),
          ...(companyId ? { company_id: companyId } : {}),
        },
      });
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao carregar reconciliação");
    } finally {
      setLoading(false);
    }
  }, [eventStatus, page, companyId]);

  useEffect(() => { load(); }, [load]);

  const handleExport = async () => {
    try {
      const params = new URLSearchParams({ event_status: eventStatus });
      if (companyId) params.set("company_id", companyId);
      const r = await api.get(`/ledger/admin/reconciliation/export?${params}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `conciliacao-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Arquivo exportado");
    } catch { toast.error("Erro ao exportar"); }
  };

  const balances = data?.balances;
  const drift = data?.drift;
  const events = data?.events?.items || [];
  const pagination = data?.events?.pagination;
  const companyBalances = data?.company_balances || [];

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="admin-reconciliation-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold tracking-tight">Conciliação StarkBank</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Comparação entre o saldo da StarkBank, o ledger Kredz e a fila de eventos.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={companyId} onValueChange={(v) => { setCompanyId(v === "__all__" ? "" : v); setPage(1); }}>
            <SelectTrigger className="w-[220px] h-9 bg-black/20 border-white/10 text-xs" data-testid="recon-company-filter">
              <Building2 className="w-3.5 h-3.5 mr-1.5 text-blue-400 shrink-0" />
              <SelectValue placeholder="Todas as empresas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todas as empresas</SelectItem>
              {companies.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button data-testid="recon-export-btn" onClick={handleExport} variant="outline" className="border-white/10 gap-2" disabled={loading}>
            <Download className="w-4 h-4" /> CSV
          </Button>
          <Button data-testid="recon-refresh-btn" onClick={() => load({ refresh: true })} disabled={loading} variant="outline" className="border-white/10 gap-2">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Atualizar
          </Button>
        </div>
      </div>

      {/* Operations Advisory Banner (Fase 5) */}
      <OperationsAdvisoryBanner />

      {/* Tab switcher */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-black/20" data-testid="recon-main-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">Visão Geral</TabsTrigger>
          <TabsTrigger value="balances" data-testid="tab-balances">Saldos por Empresa</TabsTrigger>
          <TabsTrigger value="unclassified" data-testid="tab-unclassified">Não Classificados</TabsTrigger>
        </TabsList>
      </Tabs>

      {activeTab === "unclassified" ? (
        <UnclassifiedMovements />
      ) : activeTab === "balances" ? (
        <Card className="border-white/10" data-testid="company-balances-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Building2 className="w-4 h-4 text-blue-400" /> Saldo Ledger por Empresa
              <span className="text-xs font-normal text-muted-foreground">({companyBalances.length} registros)</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {companyBalances.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">Nenhum saldo encontrado.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="company-balances-table">
                  <thead>
                    <tr className="border-b border-white/10 text-left text-xs text-muted-foreground">
                      <th className="py-2 pr-3 font-medium">Empresa</th>
                      <th className="py-2 pr-3 font-medium text-right">Créditos</th>
                      <th className="py-2 pr-3 font-medium text-right">Débitos</th>
                      <th className="py-2 pr-3 font-medium text-right">Saldo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {companyBalances.map((cb, i) => (
                      <tr key={cb.company_id || `admin-${i}`} className="border-b border-white/5 hover:bg-white/5 transition-colors"
                          data-testid={`balance-row-${cb.company_id || "admin"}`}>
                        <td className="py-2.5 pr-3 text-sm font-medium">
                          {!cb.company_id ? (
                            <span className="text-yellow-400">{cb.company_name}</span>
                          ) : cb.company_name}
                        </td>
                        <td className="py-2.5 pr-3 text-xs font-mono text-right text-emerald-400">
                          {formatCurrency(cb.credits_cents / 100)}
                        </td>
                        <td className="py-2.5 pr-3 text-xs font-mono text-right text-red-400">
                          {formatCurrency(cb.debits_cents / 100)}
                        </td>
                        <td className="py-2.5 pr-3 text-sm font-mono text-right font-semibold">
                          <span className={cb.balance_cents >= 0 ? "text-emerald-400" : "text-red-400"}>
                            {formatCurrency(cb.balance_brl)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Snapshot meta */}
          {data && (
            <div className="text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
              <span>Snapshot: <span className="font-mono">{formatTime(data.snapshot_at)}</span></span>
              <span>Cutoff (T-60s): <span className="font-mono">{formatTime(data.snapshot_cutoff_at)}</span></span>
            </div>
          )}

          {/* Health Score */}
          <HealthScoreCard score={data?.health_score} />

          {/* 4 Drift cards */}
          {drift && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="drift-cards">
              <DriftCard
                title="Ingest Lag"
                count={drift.ingest_lag?.count}
                status={drift.ingest_lag?.status || "unknown"}
                description="Transações finalizadas no StarkBank sem bank_event"
                icon={ArrowUpDown}
                dataTestId="drift-ingest-lag"
              />
              <DriftCard
                title="In-Flight"
                count={drift.in_flight?.count != null ? `${drift.in_flight.count}${drift.in_flight.total_brl ? ` · R$ ${drift.in_flight.total_brl.toFixed(2)}` : ""}` : null}
                status={drift.in_flight?.status || "ok"}
                description="Dinheiro em trânsito (processing/created)"
                icon={Activity}
                dataTestId="drift-in-flight"
              />
              <DriftCard
                title="Projeção Falhada"
                count={drift.projection_failed?.count}
                status={drift.projection_failed?.status || "unknown"}
                description="Eventos ingeridos mas não projetados no ledger"
                icon={Layers}
                dataTestId="drift-projection-failed"
              />
              <DriftCard
                title="Saldo Drift"
                count={drift.balance_drift?.brl == null ? null : `R$ ${drift.balance_drift.brl.toFixed(2)}`}
                status={drift.balance_drift?.status || "unknown"}
                description="Diferença Saldo StarkBank vs Ledger Kredz"
                icon={Scale}
                dataTestId="drift-balance"
              />
            </div>
          )}

          {/* Balance summary */}
          {balances && (
            <Card className="border-white/10" data-testid="recon-balance-summary">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Wallet className="w-4 h-4 text-blue-400" /> Composição do saldo
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-1">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Kredz (interno)</p>
                    <BalanceRow label="Ledger ativo (empresas)" value={formatCurrency(balances.ledger_total_brl)} testId="bal-ledger-total" />
                    <BalanceRow label="  ↳ Créditos" value={formatCurrency(balances.ledger_credits_cents / 100)} valueClass="text-emerald-400" testId="bal-ledger-credits" />
                    <BalanceRow label="  ↳ Débitos" value={formatCurrency(balances.ledger_debits_cents / 100)} valueClass="text-red-400" testId="bal-ledger-debits" />
                    <BalanceRow label="Admin (margem Kredz)" value={formatCurrency(balances.admin_total_brl)} testId="bal-admin-total" />
                    <BalanceRow label="Transfers pendentes" value={formatCurrency(balances.pending_transfers_brl)} testId="bal-pending" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Comparação</p>
                    <BalanceRow
                      label="Saldo esperado StarkBank"
                      value={formatCurrency(balances.expected_starkbank_balance_brl)}
                      valueClass="font-semibold"
                      testId="bal-expected-sb"
                    />
                    <BalanceRow
                      label="Saldo real StarkBank"
                      value={balances.starkbank_balance_brl == null ? "Indisponível" : formatCurrency(balances.starkbank_balance_brl)}
                      valueClass={balances.starkbank_balance_brl == null ? "text-muted-foreground italic" : "font-semibold"}
                      testId="bal-real-sb"
                    />
                    <BalanceRow
                      label="Diferença"
                      value={balances.diff_brl == null ? "—" : formatCurrency(balances.diff_brl)}
                      valueClass={`font-bold ${balances.diff_status === "ok" ? "text-emerald-400" : balances.diff_status === "warning" ? "text-yellow-400" : balances.diff_status === "critical" ? "text-red-400" : ""}`}
                      testId="bal-diff"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Events table */}
          <Card className="border-white/10" data-testid="recon-events-card">
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Banknote className="w-4 h-4 text-purple-400" /> Eventos
                  {pagination && (
                    <span className="text-xs font-normal text-muted-foreground">
                      ({pagination.total} total — página {pagination.page})
                    </span>
                  )}
                </CardTitle>
                <Tabs value={eventStatus} onValueChange={(v) => { setEventStatus(v); setPage(1); }}>
                  <TabsList className="bg-black/20" data-testid="events-status-tabs">
                    <TabsTrigger value="all" data-testid="filter-all">Todos</TabsTrigger>
                    <TabsTrigger value="pending" data-testid="filter-pending">Pendentes</TabsTrigger>
                    <TabsTrigger value="processed" data-testid="filter-processed">Processados</TabsTrigger>
                    <TabsTrigger value="failed" data-testid="filter-failed">Falhados</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {events.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8" data-testid="events-empty">
                  Nenhum evento na janela atual.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="events-table">
                    <thead>
                      <tr className="border-b border-white/10 text-left text-xs text-muted-foreground">
                        <th className="py-2 pr-3 font-medium">Quando</th>
                        <th className="py-2 pr-3 font-medium">Tipo</th>
                        <th className="py-2 pr-3 font-medium">De</th>
                        <th className="py-2 pr-3 font-medium">Para</th>
                        <th className="py-2 pr-3 font-medium">Valor</th>
                        <th className="py-2 pr-3 font-medium">Entries</th>
                        <th className="py-2 pr-3 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {events.map((e) => {
                        const cfg = EVENT_STATUS_BADGE[e.status] || EVENT_STATUS_BADGE.pending;
                        return (
                          <tr
                            key={e.id}
                            className="border-b border-white/5 hover:bg-white/5 transition-colors"
                            data-testid={`event-row-${e.id}`}
                          >
                            <td className="py-2.5 pr-3 text-xs font-mono text-muted-foreground" title={e.starkbank_created_at ? `StarkBank: ${formatTime(e.starkbank_created_at)}` : ""}>
                              {formatTime(e.starkbank_created_at || e.created_at)}
                            </td>
                            <td className="py-2.5 pr-3 text-xs font-mono truncate max-w-[180px]" title={e.kind}>
                              <span className="text-foreground">{KIND_LABELS[e.kind] || e.kind}</span>
                            </td>
                            <td className="py-2.5 pr-3 text-xs truncate max-w-[140px]" title={e.from_label || ""}>
                              {e.from_label || <span className="text-muted-foreground italic">—</span>}
                            </td>
                            <td className="py-2.5 pr-3 text-xs truncate max-w-[140px]" title={e.to_label || ""}>
                              {e.to_label || <span className="text-muted-foreground italic">—</span>}
                            </td>
                            <td className="py-2.5 pr-3 text-xs font-mono">
                              {e.amount_cents != null ? formatCurrency(e.amount_cents / 100) : <span className="text-muted-foreground">—</span>}
                            </td>
                            <td className="py-2.5 pr-3 text-xs">
                              {e.ledger_entries_count + e.admin_ledger_entries_count}
                              {e.admin_ledger_entries_count > 0 && (
                                <span className="text-muted-foreground"> ({e.admin_ledger_entries_count} admin)</span>
                              )}
                            </td>
                            <td className="py-2.5 pr-3">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className={cfg.classes} title={e.failure_message || ""}>
                                  {cfg.label}
                                </Badge>
                                {e.status === "attention" && (
                                  <span className="text-[10px] text-orange-400/80 whitespace-nowrap" data-testid={`event-cta-${e.id}`}>
                                    Reconciliar via 4-eyes
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {pagination && pagination.total > pagination.page_size && (
                <div className="flex items-center justify-end gap-2 mt-4">
                  <Button
                    data-testid="events-prev-btn"
                    variant="outline" size="sm"
                    disabled={page === 1 || loading}
                    onClick={() => setPage(page - 1)}
                    className="border-white/10"
                  >
                    Anterior
                  </Button>
                  <Button
                    data-testid="events-next-btn"
                    variant="outline" size="sm"
                    disabled={page * pagination.page_size >= pagination.total || loading}
                    onClick={() => setPage(page + 1)}
                    className="border-white/10"
                  >
                    Próximo
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
